import React, { useState, useEffect } from 'react';
import {
  GoogleGenerativeAI,
  HarmCategory,
  HarmBlockThreshold,
  ChatSession,
} from '@google/generative-ai';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import ChatMessages from './ChatMessages.tsx';
import ChatInput from './ChatInput.tsx';
import { Typography, CircularProgress } from '@mui/material';
// import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import { signOut } from 'firebase/auth';
import { auth } from '../Firebase.tsx'; 
import LogoutTwoToneIcon from '@mui/icons-material/LogoutTwoTone';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { promptWithRole } from '../details.tsx';
import weight from '../weight.png';

const API_KEY: string = process.env.REACT_APP_GEMINI_API_KEY || '';
const MODEL_NAME = "gemini-2.5-flash";
const genAI = new GoogleGenerativeAI(API_KEY);

const generationConfig = {
  temperature: 0.9,
  topK: 1,
  topP: 1,
  maxOutputTokens: 2048,
};

const safetySettings = [
  {
    category: HarmCategory.HARM_CATEGORY_HARASSMENT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
];

interface ChatBotProps {
  onSignOutSuccess: () => void;
}

const ChatBot = ({ onSignOutSuccess }: ChatBotProps) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<string[]>([]);
  const [chat, setChat] = useState<ChatSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initChat = async () => {
      try {
        const newChat = await genAI
            .getGenerativeModel({ model: MODEL_NAME })
            .startChat({
                generationConfig,
                safetySettings,
            });
        setChat(newChat);
      } catch (err) {
        setError('Failed to initialize chat.');
      }
    };
    initChat();
  }, []);

  const handleSend = async () => {
    if (input.trim() === '') return;

    const userMessage = `User: ${input}`;
    setMessages([...messages, userMessage]);

    try {
      let botMessage = '';

      if (chat) {
        const result = await chat.sendMessage(`${promptWithRole}\n\n${input}`);
        botMessage = result.response.text().replace(/\*/g, '');
      }

      // Split the bot message into paragraphs and bullet points
      const structuredBotMessages = botMessage.split(/\n\n|\n\* /).filter(part => part.trim() !== '');

      const finalBotMessages = structuredBotMessages.map((message, index) => `Bot: ${message}`);

      setMessages([...messages, userMessage, ...finalBotMessages]);
    } catch (err) {
      console.error('Error in handleSend:', err);
      const botMessage = 'Bot: Sorry, I did not understand that.';
      setMessages([...messages, userMessage, botMessage]);
    }

    setInput('');
  };

  const handleSignOut = async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      await signOut(auth);
      setLoading(true);
      setTimeout(() => {
        onSignOutSuccess();
      }, 1500);
    } catch (error) {
      setError('Error signing out.');
    }
  };

  return (
    <Container component="main" maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#fff', paddingY: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
            <Typography variant='h4' align='center' gutterBottom>FitBot Chat</Typography>
            {/* <HealthAndSafetyOutlinedIcon style={{ marginLeft: 10, marginBottom: 13 }} fontSize='large' /> */}
            <img src={weight} alt="weight" style={{ marginLeft: 10, marginBottom: 13, width: 40, height: 40 }} />
          </Box>
          <Tooltip title="Sign Out">
            <IconButton color="primary" onClick={handleSignOut} sx={{ marginLeft: 'auto', marginBottom: 1.5 }}>
              {loading ? <CircularProgress size={24} /> : <LogoutTwoToneIcon fontSize='large'/> }
            </IconButton>
          </Tooltip>
        </Box>
        {error && <Typography color="error">{error}</Typography>}
        <ChatMessages messages={messages} />
        <ChatInput input={input} setInput={setInput} handleSend={handleSend} />
      </Box>
    </Container>
  );
};

export default ChatBot;
